import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ConversationStatus, ProductDTO, QuickReplyDTO } from '@sac/contracts'
import { api, ApiError } from '../../lib/api'
import { useUi } from '../../state/UiContext'
import { qk } from '../../lib/query'
import { useToast } from '../ui/Toaster'
import { ConversationHeader } from './ConversationHeader'
import { Timeline } from './Timeline'
import { Composer } from './Composer'
import { MlQuestionNotice } from './MlQuestionNotice'
import { ProductBanner } from './ProductBanner'
import { ReminderBar } from './ReminderBar'
import { FollowUpPrompt, type FollowUpChoice } from './FollowUpPrompt'
import { Icon } from '../icons'

export function ConversationPane({
  conversationId,
  agents,
  quickReplies,
  products,
}: {
  conversationId: string
  agents: { id: string; name: string; avatarColor: string }[]
  quickReplies: QuickReplyDTO[]
  products: ProductDTO[]
}) {
  const toast = useToast()
  const { dispatch } = useUi()
  const qc = useQueryClient()

  const { data: conv } = useQuery({
    queryKey: qk.conversation(conversationId),
    queryFn: () => api.conversation(conversationId),
  })

  useEffect(() => {
    api.markRead(conversationId).catch(() => {})
  }, [conversationId])

  const patchMutation = useMutation({
    mutationFn: (body: { status?: ConversationStatus; assignedAgentId?: string | null; priority?: 'low' | 'normal' | 'high' }) =>
      api.patchConversation(conversationId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.conversation(conversationId) }),
  })

  const unreadMutation = useMutation({
    mutationFn: () => api.markUnread(conversationId),
    onSuccess: () => {
      // The list is what actually shows the flag; the open pane is refreshed too so the state
      // is not something the agent has to take on faith.
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: qk.conversation(conversationId) })
      toast('Conversa marcada como não lida.')
    },
  })

  // Shown after a reply that deliberately left the conversation open — see FollowUpPrompt.
  const [askFollowUp, setAskFollowUp] = useState(false)

  const sendMutation = useMutation({
    mutationFn: (body: {
      text?: string
      productIds?: string[]
      closeAfter?: boolean
      aiVerbatim?: boolean
      images?: { dataUrl: string; filename: string }[]
      cordialityReviewId?: string
    }) =>
      api.sendMessage(conversationId, {
        text: body.text,
        productIds: body.productIds,
        aiVerbatim: body.aiVerbatim,
        images: body.images,
        cordialityReviewId: body.cordialityReviewId,
      }),
    onSuccess: (_data, body) => {
      qc.invalidateQueries({ queryKey: qk.conversation(conversationId) })
      if (body.closeAfter) {
        // Resolving here rather than server-side keeps it an agent's decision. A channel that
        // closes its own threads (a public question) does that on its own, and this is a no-op.
        patchMutation.mutate({ status: 'resolved' })
      } else {
        setAskFollowUp(true)
      }
    },
    onError: (e, body) => {
      // A recusa do servidor diz o porquê (link bloqueado, por exemplo); e o texto volta para o
      // campo, para não sumir junto com o envio que não saiu.
      if (body.text) dispatch({ type: 'setDraft', conversationId, text: body.text })
      toast(e instanceof ApiError && e.detail ? e.detail : 'Não foi possível enviar a mensagem.')
    },
  })

  const followUp = useMutation({
    mutationFn: (choice: FollowUpChoice) =>
      api.createReminder(conversationId, {
        dueAt: choice.dueAt!.toISOString(),
        note: 'Sem resposta do cliente — retomar.',
        source: 'agent',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.reminders(conversationId) })
      qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const noteMutation = useMutation({
    mutationFn: (text: string) => api.sendNote(conversationId, text),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.conversation(conversationId) }),
  })

  const retryMutation = useMutation({
    mutationFn: (messageId: string) => api.retryMessage(messageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.conversation(conversationId) }),
  })

  const linkMutation = useMutation({
    mutationFn: (identityId: string) => api.linkIdentity(conversationId, identityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.conversation(conversationId) })
      toast('Perfis vinculados — histórico unificado')
    },
  })
  const rejectMutation = useMutation({
    mutationFn: (identityId: string) => api.rejectIdentity(conversationId, identityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.conversation(conversationId) })
      toast('Correspondência descartada')
    },
  })

  if (!conv) {
    return (
      <section className="conv">
        <div className="ph">
          <div className="ph-in">
            <h2>Carregando…</h2>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="conv">
      <ConversationHeader
        conv={conv}
        agents={agents}
        onAssign={(agentId) => patchMutation.mutate({ assignedAgentId: agentId })}
        onStatus={(status) => {
          patchMutation.mutate({ status })
          if (status === 'resolved') toast('Conversa resolvida — saiu da caixa ativa')
        }}
        onTogglePriority={() =>
          patchMutation.mutate({ priority: conv.priority === 'high' ? 'normal' : 'high' })
        }
        onResolve={() => patchMutation.mutate({ status: 'resolved' })}
        onRelink={() => toast('Buscando identidades correspondentes…')}
        onMarkUnread={() => unreadMutation.mutate()}
      />

      {conv.aiHandling && (
        <div className="composer-zone" style={{ paddingBottom: 0, borderTop: 'none' }}>
          <div className="cz-inner">
            <div className="ai-active">
              <Icon name="spark" size={14} />
              Atendimento pela IA · modo automático fora do horário comercial
              <button
                className="btn sec sm"
                onClick={() => {
                  patchMutation.mutate({ assignedAgentId: agents[0]?.id ?? null, status: 'open' })
                  toast('Você assumiu a conversa')
                }}
              >
                Assumir conversa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pinned above the thread: the question is always about a listing, and reading the
          question alone never tells you which. */}
      <ReminderBar conversationId={conv.id} />

      <ProductBanner conversationId={conv.id} />

      <Timeline
        conv={conv}
        onLinkIdentity={(id) => linkMutation.mutate(id)}
        onRejectIdentity={(id) => rejectMutation.mutate(id)}
        onRetry={(id) => retryMutation.mutate(id)}
      />

      {conv.channelType === 'mercadolivre_question' && (
        <MlQuestionNotice
          answered={conv.messages.some((m) => m.direction === 'outgoing' && m.visibility === 'customer')}
        />
      )}

      {askFollowUp && (
        <FollowUpPrompt
          onDismiss={() => setAskFollowUp(false)}
          onChoose={(choice) => {
            setAskFollowUp(false)
            if (!choice.dueAt) return
            followUp.mutate(choice)
            toast(`Lembrete criado — ${choice.label.toLowerCase()}.`)
          }}
        />
      )}

      <div className="composer-zone">
        <Composer
          conversationId={conversationId}
          channelType={conv.channelType}
          quickReplies={quickReplies}
          productsFor={products}
          onSend={(text, productIds, closeAfter, aiVerbatim, images, cordialityReviewId) => {
            sendMutation.mutate({
              cordialityReviewId,
              text: text || undefined,
              productIds: productIds.length ? productIds : undefined,
              closeAfter,
              aiVerbatim,
              images: images?.length ? images : undefined,
            })
          }}
          onSendNote={(text) => noteMutation.mutate(text)}
        />
      </div>
    </section>
  )
}
